import { getPriceHeadline, getPriceTypeLabel } from '../PricesList/priceHelpers';
import { ORDER_BUMP_COPY } from './offerCopy';

// A, A or B, A, B or C
const joinWithOr = names =>
  names.length > 1 ? `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}` : names[0];

export const priceName = price =>
  price.label || `${getPriceTypeLabel(price)} ${getPriceHeadline(price, { amountsInCents: true })}`;

const findById = (items, idOf, id) => items.find(item => String(idOf(item)) === String(id));

const LIST_FOR_SUBJECT = {
  PRICE: { key: 'prices', idOf: price => price._id, nameOf: priceName },
  TICKET: {
    key: 'ticketTypes',
    idOf: ticketType => ticketType._id,
    nameOf: ticketType => ticketType.name,
  },
  ORDER_BUMP: { key: 'otherOrderBumps', idOf: bump => bump.productId, nameOf: bump => bump.title },
};

const idsFor = logic => {
  if (logic.subject === 'ORDER_BUMP') return logic.orderBumpProductIds ?? [];
  if (logic.subject === 'TICKET') return logic.ticketTypeIds ?? [];
  return logic.priceIds ?? [];
};

/**
 * The names saved with a rule: each item's name as it is now, or the name saved
 * before if it is gone, so a deleted price, ticket or bump can still be named.
 * `logic` is a normalized rule, with its subject set.
 */
export function getRuleLabels(logic, savedLabels, lists) {
  const { key, idOf, nameOf } = LIST_FOR_SUBJECT[logic.subject];

  return idsFor(logic)
    .map(id => {
      const item = findById(lists[key] ?? [], idOf, id);
      const name = item
        ? nameOf(item)
        : (savedLabels ?? []).find(label => String(label.id) === String(id))?.name;
      return name ? { id: String(id), name } : null;
    })
    .filter(Boolean);
}

/**
 * The placement's conditional-logic rule as one sentence, so the row's
 * Conditional badge can say what the rule is without opening the row. Always
 * phrased as when the bump shows, whichever the comparison.
 *
 * It reads the rule as saved, even one that can no longer be met: a disabled
 * ticket is named like any other, and a deleted price, ticket or bump keeps its
 * place in the sentence: by the name saved with the rule and a bracketed note,
 * or by the note alone for a rule saved before names were.
 */
export default function describeBumpRule(
  logic,
  {
    prices = [],
    ticketTypes,
    otherOrderBumps = [],
    copy = ORDER_BUMP_COPY,
    markInactiveTickets = false,
  } = {},
) {
  const isNot = logic?.comparison === 'IS_NOT';
  const savedName = id =>
    (logic?.labels ?? []).find(label => String(label.id) === String(id))?.name;
  const namesFor = (ids, items, idOf, nameOf, gone, missingName, noteOf = () => null) =>
    ids.map(id => {
      const item = findById(items, idOf, id);
      if (item) return noteOf(item) ? `"${nameOf(item)}" ${noteOf(item)}` : `"${nameOf(item)}"`;
      return savedName(id) ? `"${savedName(id)}" ${gone}` : missingName;
    });

  if (logic?.subject === 'ORDER_BUMP') {
    const ids = logic.orderBumpProductIds ?? [];
    if (!ids.length) return `No ${copy.noun} picked yet, so it always shows.`;

    const names = namesFor(
      ids,
      otherOrderBumps,
      bump => bump.productId,
      bump => bump.title,
      '(removed)',
      `(removed ${copy.noun})`,
    );
    if (!isNot) return `Shows when ${joinWithOr(names)} is added.`;
    if (names.length === 1) return `Shows when ${names[0]} is not added.`;
    return `Shows when none of ${joinWithOr(names)} is added.`;
  }

  if (Array.isArray(ticketTypes)) {
    const ids = logic?.ticketTypeIds ?? [];
    if (!ids.length) return 'No ticket picked yet, so it always shows.';

    const names = namesFor(
      ids,
      ticketTypes,
      ticketType => ticketType._id,
      ticketType => ticketType.name,
      '(deleted)',
      '(deleted)',
      // A ticket rule treats a disabled or archived ticket as never selected, so it says so.
      ticketType =>
        markInactiveTickets && ['disabled', 'archived'].includes(ticketType.status)
          ? `(${ticketType.status})`
          : null,
    );
    return `Shows when ${isNot ? 'no' : 'a'} ${joinWithOr(names)} ticket is selected.`;
  }

  const priceIds = logic?.priceIds ?? [];
  if (!priceIds.length) return 'No price picked yet, so it always shows.';

  const names = namesFor(priceIds, prices, price => price._id, priceName, '(deleted)', '(deleted)');
  if (!isNot) return `Shows when the ${joinWithOr(names)} price is selected.`;
  if (names.length === 1) return `Shows when the ${names[0]} price is not selected.`;
  return `Shows when none of the ${joinWithOr(names)} prices is selected.`;
}
